/**
 * ModuleCard (P3-B Doc 37) — Hiển thị 1 Odoo module với status + activate action.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";

import type { OdooModule } from "@/api/odooModules";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  module: OdooModule;
  busy?: boolean;
  onActivate: () => void;
  onViewForms?: () => void;
}

export function ModuleCard({ module, busy = false, onActivate, onViewForms }: Props): JSX.Element {
  const isDone = module.status === "done";
  const isError = module.status === "error";
  const isActivating = module.status === "activating" || busy;
  const isCurated = module.is_curated;

  return (
    <Card
      className={cn(
        "transition-all",
        isDone && "border-emerald-300/60 dark:border-emerald-700/40",
        isError && "border-rose-300/60 dark:border-rose-700/40",
        !isCurated && "opacity-70",
      )}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="text-3xl shrink-0 leading-none mt-0.5">
            {module.icon ?? "🧩"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">
              {module.label_vi ?? module.odoo_display_name}
            </div>
            <code className="text-[10px] text-muted-foreground">
              {module.name}
            </code>
            {module.odoo_summary ? (
              <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                {module.odoo_summary}
              </p>
            ) : null}
          </div>
        </div>

        {/* Models hint */}
        {module.main_models.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {module.main_models.slice(0, 3).map((m) => (
              <code
                key={m}
                className="text-[10px] bg-muted px-1.5 py-0.5 rounded"
              >
                {m}
              </code>
            ))}
            {module.main_models.length > 3 ? (
              <span className="text-[10px] text-muted-foreground">
                +{module.main_models.length - 3}
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Status row */}
        <div className="flex items-center gap-2 min-h-[24px]">
          {isDone ? (
            <Badge
              variant="default"
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] px-1.5 py-0"
            >
              <CheckCircle2 className="size-3 mr-0.5" />
              {module.generated_form_codes.length} form
              {module.generated_form_codes.length !== 1 ? "s" : ""}
            </Badge>
          ) : isError ? (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
              <AlertTriangle className="size-3 mr-0.5" />
              Lỗi
            </Badge>
          ) : isActivating ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              <Loader2 className="size-3 mr-0.5 animate-spin" />
              Đang gen...
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {isCurated ? "Sẵn sàng" : "Custom"}
            </Badge>
          )}
        </div>

        {/* Last error (collapsed) */}
        {isError && module.last_error ? (
          <p className="text-[10px] text-destructive bg-destructive/5 border border-destructive/20 rounded px-2 py-1 line-clamp-2">
            {module.last_error}
          </p>
        ) : null}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant={isDone ? "outline" : "brand"}
            className="flex-1"
            onClick={onActivate}
            disabled={isActivating || (!isCurated && module.main_models.length === 0)}
          >
            {isActivating ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                Đang gen...
              </>
            ) : isDone ? (
              <>
                <RefreshCw className="size-3.5 mr-1" />
                Re-sync
              </>
            ) : (
              <>
                <Sparkles className="size-3.5 mr-1" />
                Activate
              </>
            )}
          </Button>
          {isDone && onViewForms ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onViewForms}
              className="text-xs"
            >
              Xem forms
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
